import { PDFDict, PDFDocument, PDFName } from 'pdf-lib';
import { OBJ } from '../constants';
import { applyImageSwaps, swapsOf } from './imageSwap';
import { loadPdfJsDoc } from './pdfEngine';
import { stripTextInRects } from './stripText';

/**
 * Deleting the paragraphs that "Edit text" replaced — for the export, and for
 * the preview behind it.
 *
 * Both need the same answer to the same question, and for a while only the
 * export had it. The export takes the old words out of the content stream and,
 * where that works, throws away the white patch that was covering them. The
 * canvas could not do either: it draws pdf.js's render of the untouched page,
 * so it kept painting the patch — and a patch is opaque. Anything the page drew
 * behind the paragraph, a watermark most visibly, was blotted out on screen and
 * present in the downloaded file. The preview disagreed with the product.
 *
 * So the strip runs twice now, from one implementation: once into the exported
 * document, once into a throwaway copy the canvas renders instead of the
 * original. What the patch used to hide, the second render simply does not
 * contain.
 */

/** Page-space (u, v) to PDF user space, for a page rotated by `rotation`. */
function mapPoint(u, v, rotation, box) {
    const { x: cx, y: cy, width: W, height: H } = box;
    switch (rotation) {
        case 90: return [cx + v, cy + u];
        case 180: return [cx + W - u, cy + v];
        case 270: return [cx + W - v, cy + H - u];
        default: return [cx + u, cy + H - v];
    }
}

/** Text boxes that stand in for a paragraph of the source. */
export const replacementsOf = (objects) => (objects || []).filter(
    (o) => o.type === OBJ.TEXT && o.replaces,
);

/**
 * Delete the source text under every replacement box on this page.
 *
 * @returns {Set<string>} ids of the boxes whose paragraph really went away.
 *   One box at a time on purpose: a paragraph drawn inside a form XObject is
 *   out of the walker's reach, and its patch has to stay even when the box
 *   beside it stripped cleanly.
 */
export function stripReplacedText(page, meta, objects) {
    const stripped = new Set();
    const replacements = replacementsOf(objects);
    if (!replacements.length) return stripped;

    const baseRotation = ((meta.baseRotation || 0) % 360 + 360) % 360;
    const cropBox = page.getCropBox();
    const box = {
        x: cropBox.x,
        y: cropBox.y,
        width: cropBox.width,
        height: cropBox.height,
    };

    for (const obj of replacements) {
        const r = obj.replaces;
        const [ax, ay] = mapPoint(r.x, r.y, baseRotation, box);
        const [bx, by] = mapPoint(r.x + r.w, r.y + r.h, baseRotation, box);
        if (stripTextInRects(page, [{
            x0: Math.min(ax, bx),
            x1: Math.max(ax, bx),
            y0: Math.min(ay, by),
            y1: Math.max(ay, by),
        }]) > 0) {
            stripped.add(obj.id);
        }
    }
    return stripped;
}


/* ------------------------------------------------------------ draft view */

/**
 * A 1×1 white pixel: what every picture becomes in draft view.
 *
 * Drawn rather than pasted in as a literal, because a literal is a string
 * nobody can read: the first one written here was red, and a red page is not
 * an obvious consequence of a base64 blob.
 */
async function whitePixelPng() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, 1, 1);
    const blob = await new Promise((resolve) => { canvas.toBlob(resolve, 'image/png'); });
    return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Point every image on the page at one white pixel.
 *
 * Word's Draft view drops the pictures and leaves the words, and that is the
 * only thing that makes it worth having: on a PDF the pictures are the slow
 * part of the render and the part nobody is proof-reading. The image objects
 * themselves are left in place and simply made to draw nothing, which keeps the
 * page's own layout — a photograph's white box still holds its column open.
 *
 * Form XObjects are walked into rather than blanked: a form is a piece of the
 * page's drawing, and blanking one would take its text with it.
 */
function blankImages(context, resources, whiteRef, seen) {
    if (!(resources instanceof PDFDict)) return;
    const xobjects = resources.lookup(PDFName.of('XObject'), PDFDict);
    if (!xobjects) return;

    for (const [name, value] of xobjects.entries()) {
        const stream = context.lookup(value);
        const subtype = stream?.dict?.get(PDFName.of('Subtype'));
        if (subtype === PDFName.of('Image')) {
            xobjects.set(name, whiteRef);
        } else if (subtype === PDFName.of('Form')) {
            const key = String(value);
            if (seen.has(key)) continue;
            seen.add(key);
            blankImages(context, stream.dict.lookup(PDFName.of('Resources'), PDFDict), whiteRef, seen);
        }
    }
}

/**
 * A copy of the document with those paragraphs removed, for the canvas to
 * render in place of the original.
 *
 * Its pages are in the editor's order rather than the source's, so a duplicated
 * page can be edited on one copy without holes appearing in the other; the page
 * to draw for row `i` is simply page `i + 1`.
 *
 * Returns null when there is nothing to strip, which is the common case — the
 * caller then keeps rendering the original and pays nothing.
 */
export async function buildStrippedPreview(sourceBytes, pages, objectsByUid, { hideImages = false } = {}) {
    if (!sourceBytes || !pages?.length) return null;
    const anyReplacement = pages.some((p) => replacementsOf(objectsByUid[p.uid]).length);
    // A swapped picture is drawn by the PAGE, not by the overlay, so the canvas
    // can only show it by rendering a document that has it.
    const anySwap = pages.some((p) => swapsOf(objectsByUid[p.uid]).length);
    if (!anyReplacement && !anySwap && !hideImages) return null;

    const src = await PDFDocument.load(sourceBytes.slice(0), { ignoreEncryption: true });
    const out = await PDFDocument.create();
    const white = hideImages ? await out.embedPng(await whitePixelPng()) : null;
    const seenForms = new Set();

    const sourceIndices = pages.filter((p) => p.sourceIndex != null).map((p) => p.sourceIndex);
    const copied = sourceIndices.length ? await out.copyPages(src, sourceIndices) : [];

    const stripped = new Set();
    let cursor = 0;
    for (const meta of pages) {
        if (meta.sourceIndex == null) {
            // A blank page has no source text to take out, but it still needs a
            // slot so row `i` keeps lining up with page `i + 1`.
            out.addPage([meta.width, meta.height]);
            continue;
        }
        const page = copied[cursor];
        cursor += 1;
        out.addPage(page);
        for (const id of stripReplacedText(page, meta, objectsByUid[meta.uid])) stripped.add(id);
        // eslint-disable-next-line no-await-in-loop
        await applyImageSwaps(out, page, meta, objectsByUid[meta.uid]);
        if (white) blankImages(out.context, page.node.Resources(), white.ref, seenForms);
    }

    if (!stripped.size && !hideImages && !anySwap) return null;

    const bytes = await out.save({ useObjectStreams: true });
    const doc = await loadPdfJsDoc(new Blob([bytes], { type: 'application/pdf' }));
    return { doc, stripped };
}
